import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserType, type AuthUser } from "@platform/auth";
import { type EducationMemberOption } from "@platform/billing";
import { PATHS } from "@/shared/constants/app/routes.constant.ts";
import { useAuth } from "@/features/auth";
import { useEducationMemberOptions } from "../api/use-education.query";
import { useEducationCart } from "../model/education-cart.context";
import type {
  CheckoutOrderer,
  SelectedAttendee,
} from "../model/education-checkout.type";
import { useEducationCheckout } from "../hooks/useEducationCheckout";
import { EducationProgressSteps } from "../components/EducationProgressSteps";
import { CheckoutOrdererSection } from "../components/checkout/CheckoutOrdererSection";
import { CheckoutPersonalAttendeeSection } from "../components/checkout/CheckoutPersonalAttendeeSection";
import { CheckoutBusinessAttendeeSection } from "../components/checkout/CheckoutBusinessAttendeeSection";
import { CheckoutSummary } from "../components/checkout/CheckoutSummary";
import { EDUCATION_STEP } from "../constants/education-step.constant";
import { memberSessionState } from "../lib/education-member";

const profileOrderer = (user: AuthUser | null): CheckoutOrderer => ({
  ordererName: user?.name ?? "",
  ordererPhone: user?.phone ?? "",
  ordererEmail: user?.email ?? "",
  ordererCompanyName: "",
});

/** 교육 접수 — 개인은 본인 1명, 기업은 활성 구성원을 회차별로 선택한다. */
export function EducationCheckoutView() {
  const { items } = useEducationCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { submit, submitting } = useEducationCheckout();
  const isBusiness = user?.userType === UserType.BUSINESS;
  // 장바구니의 회차를 함께 물어봐야 이미 접수된 사람을 잠글 수 있다.
  const cartSessionIds = useMemo(
    () => items.map((item) => item.sessionId),
    [items],
  );
  const memberOptions = useEducationMemberOptions(isBusiness, cartSessionIds);
  const [editingPersonalOrderer, setEditingPersonalOrderer] = useState(false);

  const [attendeesBySession, setAttendeesBySession] = useState<
    Record<string, SelectedAttendee[]>
  >(() => Object.fromEntries(items.map((item) => [item.sessionId, []])));
  const [orderer, setOrderer] = useState(() => profileOrderer(user));

  // 인증 캐시가 첫 렌더 뒤 채워지는 경우에도 개인 기본값과 기업명을 한 번 보정한다.
  useEffect(() => {
    if (!user) return;
    setOrderer((current) => ({
      ...current,
      ordererName: current.ordererName || user.name,
      ordererPhone: current.ordererPhone || user.phone || "",
      ordererEmail: current.ordererEmail || user.email,
      ordererCompanyName:
        current.ordererCompanyName ||
        memberOptions.data?.companyName ||
        "",
    }));
    if (isBusiness) {
      setAttendeesBySession((current) =>
        Object.fromEntries(
          items.map((item) => [
            item.sessionId,
            (current[item.sessionId] ?? []).filter(
              (attendee) => !!attendee.sourceUserId,
            ),
          ]),
        ),
      );
    } else {
      setAttendeesBySession(() =>
        Object.fromEntries(items.map((item) => [item.sessionId, []])),
      );
    }
  }, [isBusiness, items, memberOptions.data?.companyName, user]);

  /**
   * 기업 접수의 회차별 수강자 기본값 = **신청자 본인**.
   *
   * 대부분 신청자도 함께 듣는다. 매 회차 자기 이름부터 찾아 체크하게 두면 인원이
   * 늘어날수록 같은 조작이 반복된다. 빼려면 체크를 풀면 되고, 한 번 푼 뒤에는 다시
   * 넣지 않는다 — 그래서 최초 1회만 심는다(`seededSelf`).
   */
  const seededSelf = useRef(false);
  useEffect(() => {
    if (!isBusiness || seededSelf.current) return;
    const self = memberOptions.data?.members.find(
      (member) => member.userId === user?.id,
    );
    if (!self) return;

    seededSelf.current = true;
    setAttendeesBySession((current) =>
      Object.fromEntries(
        items.map((item) => [
          item.sessionId,
          (current[item.sessionId] ?? []).length > 0
            ? current[item.sessionId]
            : memberSessionState(self, item.sessionId)
              ? []
              : [
                {
                  sourceUserId: self.userId,
                  companyName: memberOptions.data?.companyName,
                  name: self.name,
                  phone: self.phone,
                  email: self.email,
                },
              ],
        ]),
      ),
    );
  }, [isBusiness, items, memberOptions.data, user?.id]);

  const total = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum +
          item.price *
            (isBusiness
              ? (attendeesBySession[item.sessionId]?.length ?? 0)
              : 1),
        0,
      ),
    [attendeesBySession, isBusiness, items],
  );
  const missingAttendees =
    isBusiness &&
    items.some(
      (item) => (attendeesBySession[item.sessionId]?.length ?? 0) === 0,
    );

  /**
   * 기업 접수는 신청자 입력칸이 없으므로, 계정에 연락처·이메일이 비어 있으면 화면에서
   * 고칠 방법이 없다. 그대로 보내면 서버 검증에 걸려 원인을 알 수 없는 오류가 되므로
   * 여기서 막고 프로필로 보낸다.
   */
  const ordererIncomplete =
    isBusiness &&
    (!orderer.ordererName || !orderer.ordererPhone || !orderer.ordererEmail);

  const addMember = (sessionId: string, member: EducationMemberOption) =>
    setAttendeesBySession((previous) => {
      const current = previous[sessionId] ?? [];
      if (current.some((attendee) => attendee.sourceUserId === member.userId))
        return previous;
      return {
        ...previous,
        [sessionId]: [
          ...current,
          {
            sourceUserId: member.userId,
            companyName: memberOptions.data?.companyName,
            name: member.name,
            phone: member.phone,
            email: member.email,
          },
        ],
      };
    });

  /**
   * 첫 회차 명단을 나머지 회차에 그대로 복사한다. 같은 팀이 연속 회차를 함께 듣는
   * 경우가 대부분이라, 이게 없으면 회차 수 × 인원 수만큼 같은 선택을 반복하게 된다.
   *
   * 기준을 **첫 회차로 고정**한다 — 회차마다 버튼을 두면 어느 명단이 퍼지는지가
   * 누른 위치에 따라 달라져, 실수로 눌렀을 때 무엇을 덮어썼는지 되짚기 어렵다.
   */
  const firstSessionId = items[0]?.sessionId;
  const applyFirstSessionToAll = () =>
    setAttendeesBySession((previous) => {
      if (!firstSessionId) return previous;
      const source = previous[firstSessionId] ?? [];
      const membersById = new Map(
        (memberOptions.data?.members ?? []).map((member) => [
          member.userId,
          member,
        ]),
      );
      return Object.fromEntries(
        items.map((item) => [
          item.sessionId,
          item.sessionId === firstSessionId
            ? source
            : source
                .filter((attendee) => {
                  const member = attendee.sourceUserId
                    ? membersById.get(attendee.sourceUserId)
                    : undefined;
                  // 결제 완료·대기 모두 서버가 막으므로 복사 대상에서 뺀다.
                  return !member || !memberSessionState(member, item.sessionId);
                })
                .map((attendee) => ({ ...attendee })),
        ]),
      );
    });

  const removeMember = (sessionId: string, userId: string) =>
    setAttendeesBySession((previous) => ({
      ...previous,
      [sessionId]: (previous[sessionId] ?? []).filter(
        (attendee) => attendee.sourceUserId !== userId,
      ),
    }));

  const resetOrderer = () => {
    setOrderer(profileOrderer(user));
    setEditingPersonalOrderer(false);
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (missingAttendees || ordererIncomplete) return;
    void submit({
      ...orderer,
      orgId: isBusiness ? memberOptions.data?.orgId : undefined,
      ordererCompanyName: isBusiness
        ? orderer.ordererCompanyName ||
          memberOptions.data?.companyName ||
          undefined
        : undefined,
      items: items.map((item) => ({
        sessionId: item.sessionId,
        attendees: isBusiness
          ? (attendeesBySession[item.sessionId] ?? []).map(
              ({ sourceUserId: _sourceUserId, ...attendee }) => ({
                ...attendee,
                companyName: attendee.companyName || undefined,
                email: attendee.email || undefined,
                position: attendee.position || undefined,
                note: attendee.note || undefined,
              }),
            )
          : [
              {
                name: orderer.ordererName,
                phone: orderer.ordererPhone,
                email: orderer.ordererEmail || undefined,
              },
            ],
      })),
    });
  };

  if (items.length === 0)
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <p className="text-sm text-gray-500 dark:text-neutral-400">
          선택한 회차가 없습니다.
        </p>
        <Link
          to={PATHS.EDUCATION}
          className="mt-2 inline-block text-sm font-medium text-brand-600 underline"
        >
          교육 둘러보기
        </Link>
      </div>
    );

  const ordererLocked = !isBusiness && !editingPersonalOrderer;

  return (
    <form
      onSubmit={onSubmit}
      className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12"
    >
      <header className="border-b border-gray-200 pb-7 dark:border-white/10">
        <Link
          to={PATHS.EDUCATION}
          viewTransition
          className="inline-flex items-center text-sm font-medium text-gray-500 transition-colors hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-neutral-400 dark:hover:text-white"
        >
          <span aria-hidden className="mr-1.5">
            ←
          </span>
          회차 선택으로 돌아가기
        </Link>
        <div className="mt-5">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight text-gray-950 dark:text-neutral-100">
                교육 신청서
              </h1>
              <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                {isBusiness ? "기업 접수" : "개인 접수"}
              </span>
            </div>
            <p className="mt-2 text-sm text-gray-500 dark:text-neutral-400">
              {isBusiness
                ? "회차별 수강 구성원을 선택해 주세요. 신청자는 로그인 계정으로 접수됩니다."
                : "신청자와 수강자 정보를 확인한 뒤 접수를 완료해 주세요."}
            </p>
          </div>
        </div>
      </header>

      <div className="mt-6">
        {/* 2단계(회차)로 되돌아갈 땐 담아둔 첫 회차의 과정을 편다 — 목록부터 다시 고르지 않게. */}
        <EducationProgressSteps
          activeStep={EDUCATION_STEP.CHECKOUT}
          maxSelectableStep={EDUCATION_STEP.SESSION}
          onStepSelect={(step) =>
            navigate(
              step === EDUCATION_STEP.SESSION && items[0]
                ? `${PATHS.EDUCATION}?course=${items[0].courseId}`
                : PATHS.EDUCATION,
            )
          }
        />
      </div>

      <div className="mt-8 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_20rem] xl:gap-12">
        <div className="min-w-0 space-y-8">
          {/*
           * 기업 접수는 신청자를 **입력받지 않는다** — 로그인 계정이 곧 신청자다.
           * 소속 구성원을 대신 접수하는 자리라 신청자를 손으로 고칠 수 있으면 명부와
           * 결제 주체가 갈린다. 값은 계정에서 채워 그대로 전송하고, 확인은 우측 요약에서 한다.
           */}
          {!isBusiness && (
            <CheckoutOrdererSection
              orderer={orderer}
              locked={ordererLocked}
              editing={editingPersonalOrderer}
              disabled={submitting}
              onChange={(patch) =>
                setOrderer((previous) => ({ ...previous, ...patch }))
              }
              onEdit={() => setEditingPersonalOrderer(true)}
              onReset={resetOrderer}
            />
          )}

          {!isBusiness && <CheckoutPersonalAttendeeSection orderer={orderer} />}

          {isBusiness && (
            <CheckoutBusinessAttendeeSection
              items={items}
              attendeesBySession={attendeesBySession}
              members={memberOptions.data?.members ?? []}
              membersLoading={memberOptions.isLoading}
              membersError={memberOptions.isError}
              disabled={submitting}
              onAddMember={addMember}
              onRemoveMember={removeMember}
              onApplyFirstToAll={applyFirstSessionToAll}
            />
          )}
        </div>

        <CheckoutSummary
          items={items}
          isBusiness={isBusiness}
          orderer={orderer}
          companyName={
            orderer.ordererCompanyName || memberOptions.data?.companyName || ""
          }
          attendeeCount={(sessionId) =>
            isBusiness ? (attendeesBySession[sessionId]?.length ?? 0) : 1
          }
          total={total}
          missingAttendees={missingAttendees}
          ordererIncomplete={ordererIncomplete}
          submitting={submitting}
          submitDisabled={
            submitting ||
            missingAttendees ||
            ordererIncomplete ||
            (isBusiness && !memberOptions.data)
          }
        />
      </div>
    </form>
  );
}
